import { useTranslation } from 'react-i18next';
import type { CreditCardAccount } from '@/lib/payments/creditCardAccount.types';
import { CreditCardRow } from './CreditCardRow';
import { PaymentAccountSection } from './PaymentAccountSection';

interface CreditCardListSectionProps {
  cards: CreditCardAccount[];
  isLoading?: boolean;
  onAddCard: () => void;
  onSetDefault: (id: string) => void;
  settingDefaultId?: string;
  onEdit: (id: string) => void;
  onRemove: (card: CreditCardAccount) => void;
  /** Payment_Profile__c ids with a scheduled one-time payment — see selectPaymentProfileIdsWithUpcomingPayment. */
  paymentProfileIdsWithUpcomingPayment?: Set<string>;
}

export function CreditCardListSection({
  cards,
  isLoading = false,
  onAddCard,
  onSetDefault,
  settingDefaultId,
  onEdit,
  onRemove,
  paymentProfileIdsWithUpcomingPayment,
}: CreditCardListSectionProps) {
  const { t } = useTranslation('members');

  return (
    <PaymentAccountSection
      heading={t('payments.pages.paymentAccounts.creditCardsTitle')}
      addLabel={t('payments.pages.paymentAccounts.addCard')}
      onAdd={onAddCard}
      disclaimer={t('payments.pages.paymentAccounts.creditCards.expiredDisclaimer')}
      isLoading={isLoading}
      isEmpty={cards.length === 0}
      emptyIcon="creditCard"
      emptyHeading={t('payments.pages.paymentAccounts.creditCards.emptyStateHeading')}
      emptyBody={t('payments.pages.paymentAccounts.creditCards.emptyStateBody')}
    >
      {cards.map((card) => (
        <CreditCardRow
          key={card.id}
          card={card}
          onSetDefault={onSetDefault}
          isSettingDefault={settingDefaultId === card.id}
          onEdit={onEdit}
          onRemove={onRemove}
          hasUpcomingPayment={paymentProfileIdsWithUpcomingPayment?.has(card.id)}
        />
      ))}
    </PaymentAccountSection>
  );
}
